import type { StringValue } from "ms";

import jwt, { Secret, SignOptions } from "jsonwebtoken";

// JWT Payload interface
export interface JWTPayload {
  userId: string;
  email: string;
  role: string;
  isEmailVerified: boolean;
  isProfileCompleted: boolean;
  status: string;
  iat?: number;
  exp?: number;
}

// Token pair interface
export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

// Token configuration
const ACCESS_TOKEN_EXPIRY: StringValue = (process.env.ACCESS_TOKEN_EXPIRY ??
  "15m") as StringValue;
const REFRESH_TOKEN_EXPIRY: StringValue = (process.env.REFRESH_TOKEN_EXPIRY ??
  "7d") as StringValue;

if (!process.env.JWT_SECRET || !process.env.JWT_REFRESH_SECRET) {
  throw new Error("JWT secrets must be defined in environment variables");
}

const JWT_SECRET: Secret = process.env.JWT_SECRET;
const JWT_REFRESH_SECRET: Secret = process.env.JWT_REFRESH_SECRET;

/**
 * Generate access token
 */
export const generateAccessToken = (
  payload: Omit<JWTPayload, "iat" | "exp">,
): string => {
  const options: SignOptions = {
    expiresIn: ACCESS_TOKEN_EXPIRY,
    issuer: "covalent-backend",
    audience: "covalent-frontend",
  };
  return jwt.sign(payload, JWT_SECRET, options);
};

/**
 * Generate refresh token
 */
export const generateRefreshToken = (userId: string): string => {
  const options: SignOptions = {
    expiresIn: REFRESH_TOKEN_EXPIRY,
    issuer: "covalent-backend",
    audience: "covalent-frontend",
  };
  return jwt.sign({ userId, type: "refresh" }, JWT_REFRESH_SECRET, options);
};

/**
 * Generate both access and refresh tokens
 */
export const generateTokenPair = (
  payload: Omit<JWTPayload, "iat" | "exp">,
): TokenPair => {
  const accessToken = generateAccessToken(payload);
  const refreshToken = generateRefreshToken(payload.userId);

  return {
    accessToken,
    refreshToken,
  };
};

/**
 * Verify access token
 */
export const verifyAccessToken = (token: string): JWTPayload => {
  try {
    const decoded = jwt.verify(token, JWT_SECRET, {
      issuer: "covalent-backend",
      audience: "covalent-frontend",
    }) as JWTPayload;

    return decoded;
  } catch (error) {
    if (error instanceof jwt.TokenExpiredError) {
      throw new TypeError("Access token expired");
    }
    if (error instanceof jwt.JsonWebTokenError) {
      throw new TypeError("Invalid access token");
    }
    throw new Error("Token verification failed");
  }
};

/**
 * Verify refresh token
 */
export const verifyRefreshToken = (
  token: string,
): { userId: string; type: string } => {
  try {
    const decoded = jwt.verify(token, JWT_REFRESH_SECRET, {
      issuer: "covalent-backend",
      audience: "covalent-frontend",
    }) as { userId: string; type: string };

    if (decoded.type !== "refresh") {
      throw new Error("Invalid token type");
    }

    return decoded;
  } catch (error) {
    if (error instanceof jwt.TokenExpiredError) {
      throw new TypeError("Refresh token expired");
    }
    if (error instanceof jwt.JsonWebTokenError) {
      throw new TypeError("Invalid refresh token");
    }
    throw new Error("Token verification failed");
  }
};

/**
 * Extract token from authorization header
 */
export const extractToken = (authHeader: string | undefined): string => {
  if (!authHeader) {
    throw new Error("Authorization header is required");
  }

  const parts = authHeader.split(" ");
  if (parts.length !== 2 || parts[0] !== "Bearer") {
    throw new Error("Invalid authorization header format. Use: Bearer <token>");
  }

  return parts[1];
};

/**
 * Check if token is expired (without throwing error)
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    jwt.verify(token, JWT_SECRET);
    return false;
  } catch (error) {
    if (error instanceof jwt.TokenExpiredError) {
      return true;
    }
    return false;
  }
};

/**
 * Decode token without verification (for debugging)
 */
export const decodeToken = (token: string): jwt.JwtPayload | string | null => {
  try {
    return jwt.decode(token);
  } catch {
    return null;
  }
};

/**
 * Get token expiration time
 */
export const getTokenExpiration = (token: string): Date | null => {
  try {
    const decoded = jwt.decode(token) as jwt.JwtPayload;
    if (
      decoded &&
      typeof decoded === "object" &&
      "exp" in decoded &&
      decoded.exp
    ) {
      return new Date(decoded.exp * 1000);
    }
    return null;
  } catch {
    return null;
  }
};
